import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteChildrenProps } from 'react-router-dom';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import { ProviderSelection } from 'src/modules/providers/components/ProviderSelection';
import { useAddProviderMutation } from 'src/modules/providers/documents/addProviderMutation.generated';
import {
    getCoachesAvailableToChoose,
    getMyProvidersWithPammFilter,
} from 'src/modules/providers/selectors/providersSelectors';
import { ProviderType } from 'src/modules/user/models/user';
import { getConfigForProviderType } from 'src/modules/providers/helpers/getConfigForProviderType';
import { getSessionUtilizationInfo } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { useProvider } from 'src/modules/providers/hooks/useProvider';

// [Important] TODO: Add component tests -- https://joinmodernhealth.atlassian.net/browse/FOUNDATION-198
const ScheduleSessionCoachSelectionPage: React.FC<RouteChildrenProps> = ({ history }) => {
    const { t } = useTranslation();
    const flags = useFlags();

    const [selectedProvider, setSelectedProvider] = useState('');
    const [isLoadingProviders, setIsLoadingProviders] = useState(true);
    const myProviders = useSelector(getMyProvidersWithPammFilter);
    const coachesAvailableToChoose = useSelector(getCoachesAvailableToChoose);
    const {
        coveredCoachingVisits,
        coachingVisitsRemaining,
        therapyVisitsRemaining,
        coveredTherapyVisits,
    } = useSelector(getSessionUtilizationInfo);
    const user = useSelector(getUserInformation);
    const { fetchMyProviders } = useProvider();

    const dispatch = useDispatch<Dispatch>();

    const [addProvider] = useAddProviderMutation({ client: appClient });

    const alreadyMatchedMode = myProviders.length > 0;

    const providersToRender = alreadyMatchedMode ? myProviders : coachesAvailableToChoose;

    const amountOfCoaches = providersToRender.filter(
        provider => provider.providerType === ProviderType.COACH,
    ).length;
    const amountOfTherapists = providersToRender.filter(
        provider => provider.providerType === ProviderType.THERAPIST,
    ).length;

    const providerType =
        amountOfCoaches > 0 && amountOfTherapists > 0
            ? 'provider'
            : amountOfTherapists > 0
            ? 'therapist'
            : 'coach';

    const providerTypeToSessionsLeft = {
        [ProviderType.COACH]: coachingVisitsRemaining,
        [ProviderType.THERAPIST]: therapyVisitsRemaining,
    };

    const providerTypeToCoveredVisits = {
        [ProviderType.COACH]: coveredCoachingVisits,
        [ProviderType.THERAPIST]: coveredTherapyVisits,
    };

    const goToNextStep = (providerUuid: string, useReplace?: boolean) => {
        const provider = myProviders.find(({ uuid }) => uuid === providerUuid);
        const sessionsLeft = getConfigForProviderType(provider, providerTypeToSessionsLeft, 0);
        const sessionsCovered = getConfigForProviderType(provider, providerTypeToCoveredVisits, 0);
        const navigateFunc = useReplace ? history.replace : history.push;

        navigateFunc('/schedule-session/calendar', {
            coachUuid: providerUuid,
            isFirstSession: sessionsLeft === sessionsCovered,
        });
    };

    /**Fetch last changes of providers in case the
     * displayOnSchedule has changed
     */
    useEffect(() => {
        const refreshMyProviders = async () => {
            await fetchMyProviders();
        };

        refreshMyProviders()
            .then(() => setIsLoadingProviders(false))
            .catch(() => setIsLoadingProviders(false));
    }, [fetchMyProviders]);

    useEffect(() => {
        if (isLoadingProviders === false) {
            if (myProviders.length === 1) {
                goToNextStep(myProviders[0].uuid, true);
            }

            if (myProviders.length === 0) {
                history.replace('/work-with-a-coach');
            }
        }
    }, [myProviders, isLoadingProviders]);

    useEffect(() => {
        if (flags?.hideSessionSchedulingWeb) {
            history.replace('/dashboard');
        }
    }, [flags]);

    const handleProviderPress = async (providerUuid: string) => {
        if (selectedProvider) {
            return;
        }

        if (alreadyMatchedMode) {
            const provider = myProviders.find(({ uuid }) => uuid === providerUuid);
            if (provider?.providerType) {
                const isTherapy = provider.providerType === ProviderType.THERAPIST;
                const sessionsLeft = isTherapy ? therapyVisitsRemaining : coachingVisitsRemaining;
                const sessionsRemaining = sessionsLeft > 0;

                if (
                    !sessionsRemaining &&
                    isTherapy &&
                    user.isCompanyHpiEnabled &&
                    !user.hpiEligibilityStatus
                ) {
                    return history.push('/extend-therapy-sessions');
                }
            }
        } else {
            setSelectedProvider(providerUuid);

            try {
                await addProvider({ variables: { providerUuid } });
                dispatch.providers.fetchMyProviders();
            } catch {
                toastNotificationsService.renderErrorToast(config.messages.genericError);

                return;
            } finally {
                setSelectedProvider('');
            }
        }

        goToNextStep(providerUuid);
    };

    if (myProviders.length === 1) {
        return null;
    }

    return providersToRender.length ? (
        <ProviderSelection
            providers={providersToRender}
            onProviderPress={handleProviderPress}
            selectedProvider={selectedProvider}
            alreadyMatchedMode={alreadyMatchedMode}
            headerText={t(`providers:provider_selection.title.matched.${providerType}`)}
            subTitleText={t(`providers:provider_selection.description.${providerType}`)}
        />
    ) : (
        <CenteredSpinner />
    );
};

export default ScheduleSessionCoachSelectionPage;
